import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Session } from '@supabase/auth-js';
import { supabase } from './supabase';
import { kochenLeeren } from '../kochen/speicher';
import { uhr } from '../kochen/uhr';

/** Service-Worker-Caches mit Familiendaten (vite.config.ts, runtimeCaching): beim Abmelden und Personenwechsel weg. */
export const DATEN_CACHES = ['nebenbei-daten-v1', 'nebenbei-rezepte-v1'];
export const BILD_CACHE = 'nebenbei-bilder-v1';

async function cachesLeeren(mitBildern: boolean): Promise<void> {
  // Kochsitzungen und Timer dieses Geraets (W8) gehoeren zur Person wie die Daten-Caches
  kochenLeeren();
  uhr.neuLaden();
  if (!('caches' in window)) return;
  await Promise.all([...DATEN_CACHES, ...(mitBildern ? [BILD_CACHE] : [])].map((c) => caches.delete(c)));
}

/**
 * Offline (Phase 6): ist das Zugriffstoken abgelaufen und kein Netz da, liefert getSession keine Sitzung, obwohl
 * sie im Speicher liegt (auth-js loescht sie bei Netzfehlern nicht). Dann gilt die gespeicherte Sitzung weiter,
 * damit Liste und Rezepte lesbar bleiben. Sobald wieder Netz da ist, erneuert auth-js das Token wie gewohnt.
 */
export function gespeicherteSitzung(): Session | null {
  try {
    const roh = localStorage.getItem('nebenbei2-auth');
    if (!roh) return null;
    const s = JSON.parse(roh) as Partial<Session> | null;
    return s && typeof s.access_token === 'string' && s.user && typeof s.user.id === 'string' ? (s as Session) : null;
  } catch {
    return null;
  }
}

function offline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

type AuthZustand =
  | { status: 'laedt' }
  /** bewusst: per Knopf abgemeldet (dann nach der Anmeldung auf Start, nicht zurueck zur alten Seite) */
  | { status: 'abgemeldet'; bewusst: boolean }
  | { status: 'angemeldet'; session: Session };

interface AuthKontext {
  zustand: AuthZustand;
  anmelden: (email: string, passwort: string) => Promise<{ fehler: string | null }>;
  abmelden: () => Promise<void>;
}

const Kontext = createContext<AuthKontext | null>(null);

/** Uebersetzt Supabase-Fehler in kurze deutsche Saetze, ohne interne Details. */
function fehlerText(code: string | undefined, status: number | undefined): string {
  if (code === 'invalid_credentials') return 'E-Mail oder Passwort stimmen nicht.';
  if (code === 'email_not_confirmed') return 'Diese E-Mail ist noch nicht bestätigt.';
  if (code === 'over_request_rate_limit' || status === 429) return 'Zu viele Versuche. Warte kurz und probier es dann noch mal.';
  if (status === undefined || status === 0) return 'Keine Verbindung. Prüf dein Netz und versuch es noch mal.';
  return 'Anmelden hat nicht geklappt. Versuch es gleich noch mal.';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [zustand, setZustand] = useState<AuthZustand>({ status: 'laedt' });
  const abfragen = useQueryClient();
  const letzteNutzerId = useRef<string | null>(null);
  const bewusstAbgemeldet = useRef(false);

  // Personenwechsel oder Abmelden: alle nutzerbezogenen Daten aus dem Cache werfen
  useEffect(() => {
    const id = zustand.status === 'angemeldet' ? zustand.session.user.id : null;
    if (zustand.status === 'laedt') return;
    if (letzteNutzerId.current !== null && id !== letzteNutzerId.current) {
      abfragen.removeQueries({ queryKey: ['nutzer'] });
      // Offline-Kopien (Service Worker) gehoeren der vorigen Person, Bilder auch (Familienrezepte)
      void cachesLeeren(true);
    }
    letzteNutzerId.current = id;
  }, [zustand, abfragen]);

  useEffect(() => {
    let aktiv = true;
    // auth-js loescht die gespeicherte Sitzung nur bei echtem Abmelden oder ungueltigem Refresh-Token, nie bei
    // Netzfehlern. Liegt sie noch im Speicher, gilt sie weiter (navigator.onLine ist dafuer zu unzuverlaessig).
    const ohneNetz = (): Session | null => (bewusstAbgemeldet.current ? null : gespeicherteSitzung());
    // Ohne Netz versucht auth-js ein abgelaufenes Token mit Pausen zu erneuern; so lange soll die App nicht
    // "Laedt" zeigen. Nach 1 s (offline) bzw. 6 s gilt die gespeicherte Sitzung.
    const frist = new Promise<{ data: { session: Session | null }; error: Error | null }>((r) =>
      setTimeout(() => r({ data: { session: null }, error: new Error('frist') }), offline() ? 1000 : 6000),
    );
    void Promise.race([supabase.auth.getSession(), frist])
      .then(({ data, error }) => {
        if (!aktiv) return;
        const session = data.session ?? (error ? gespeicherteSitzung() : null);
        setZustand(session ? { status: 'angemeldet', session } : { status: 'abgemeldet', bewusst: false });
      })
      .catch(() => {
        if (!aktiv) return;
        const session = gespeicherteSitzung();
        setZustand(session ? { status: 'angemeldet', session } : { status: 'abgemeldet', bewusst: false });
      });
    const { data } = supabase.auth.onAuthStateChange((_ereignis, session) => {
      if (session) bewusstAbgemeldet.current = false;
      const s = session ?? ohneNetz();
      setZustand(s ? { status: 'angemeldet', session: s } : { status: 'abgemeldet', bewusst: bewusstAbgemeldet.current });
    });
    return () => {
      aktiv = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const wert = useMemo<AuthKontext>(
    () => ({
      zustand,
      anmelden: async (email, passwort) => {
        try {
          const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: passwort });
          return { fehler: error ? fehlerText(error.code, error.status) : null };
        } catch {
          return { fehler: fehlerText(undefined, undefined) };
        }
      },
      abmelden: async () => {
        bewusstAbgemeldet.current = true;
        await cachesLeeren(false);
        // Ohne Netz kann der Server die Sitzung nicht beenden; lokal abmelden reicht dann (Token laeuft ab)
        await supabase.auth.signOut(offline() ? { scope: 'local' } : undefined);
      },
    }),
    [zustand],
  );

  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

export function useAuth(): AuthKontext {
  const k = useContext(Kontext);
  if (!k) throw new Error('useAuth ausserhalb von AuthProvider');
  return k;
}
